import { css } from '@emotion/react';
import {
  Box,
  COLOR_PALETTE,
  Flex,
  OptimizeImage,
  Stack,
  Text,
  Title,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';

const styleDescText = css({
  fontSize: 18,
  fontWeight: 500,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 14,
  },
});

const AdvantagesSection = () => {
  const { t } = useTranslation();

  const ADVANTAGES: { bgColor: string; title: string; description: ReactNode; imageUrl: string }[] =
    [
      {
        bgColor: COLOR_PALETTE.yellow[0],
        title: t('open.knowledge.advantage.revenue.title'),
        description: (
          <>
            <Text
              component="span"
              c="gray.7"
              fz={18}
              fw={500}
              lh={1.5}
              styles={{
                root: styleDescText,
              }}
            >
              <Trans
                i18nKey="open.knowledge.advantage.revenue.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/3d_icon_money_pocket.png`,
      },
      {
        bgColor: COLOR_PALETTE.green[0],
        title: t('open.knowledge.advantage.drm.title'),
        description: (
          <>
            <Text
              component="span"
              c="gray.7"
              fz={18}
              fw={500}
              lh={1.5}
              styles={{
                root: styleDescText,
              }}
            >
              <Trans
                i18nKey="open.knowledge.advantage.drm.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/public/static-images/1e08c6cc-9228-4d18-a96f-3629c8133daf.webp`,
      },
      {
        bgColor: COLOR_PALETTE.indigo[0],
        title: t('open.knowledge.advantage.ai.title'),
        description: (
          <>
            <Text
              component="span"
              c="gray.7"
              fz={18}
              fw={500}
              lh={1.5}
              styles={{
                root: styleDescText,
              }}
            >
              <Trans
                i18nKey="open.knowledge.advantage.ai.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/3d_icon_magic.png`,
      },
      {
        bgColor: COLOR_PALETTE.cyan[0],
        title: t('open.knowledge.advantage.global.title'),
        description: (
          <>
            <Text
              component="span"
              c="gray.7"
              fz={18}
              fw={500}
              lh={1.5}
              styles={{
                root: styleDescText,
              }}
            >
              <Trans
                i18nKey="open.knowledge.advantage.global.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/3d_icon_map.png`,
      },
    ];

  return (
    <Box component="section" css={styleSection}>
      <Flex
        direction="column"
        align="center"
        styles={{
          root: styleContainer,
        }}
      >
        <Stack
          gap={40}
          align="center"
          styles={{
            root: styleContents,
          }}
        >
          <Title
            order={2}
            ta="center"
            c="dark.9"
            fz={34}
            lh={1.3}
            styles={{
              root: styleSectionTitle,
            }}
          >
            <Trans i18nKey="open.knowledge.advantage.title" components={{ br: <br /> }} />
          </Title>
          <Box css={styleInnerContainer}>
            <Stack
              styles={{
                root: styleCards,
              }}
            >
              {ADVANTAGES.map((advantage) => (
                <Flex
                  key={advantage.title}
                  styles={{
                    root: styleCard(advantage.bgColor),
                  }}
                >
                  <Box css={styleCardImage}>
                    {advantage.imageUrl ? (
                      <OptimizeImage src={advantage.imageUrl} w={100 * 2} css={styleImage} />
                    ) : (
                      <Box css={styleImagePlaceholder} />
                    )}
                  </Box>
                  <Stack
                    gap={12}
                    styles={{
                      root: styleCardTextContent,
                    }}
                  >
                    <Text
                      c="gray.9"
                      fz={20}
                      fw={700}
                      lh={1.3}
                      styles={{
                        root: styleCardTitle,
                      }}
                    >
                      {advantage.title}
                    </Text>
                    <Box css={styleCardDesc}>{advantage.description}</Box>
                  </Stack>
                </Flex>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
};

export default AdvantagesSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  padding: '100px 0',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  maxWidth: 1200,
  width: '100%',
});

const styleSectionTitle = css({
  fontSize: 34,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 24,
  },
});

const styleInnerContainer = css({
  width: '100%',
});

const styleCards = css({
  maxWidth: 1200,
  width: '100%',
  gap: 24,
  [desktopFirstMediaQuery.mobile]: {
    gap: 8,
  },
});

const styleCard = (bgColor: string) =>
  css({
    position: 'relative',
    backgroundColor: bgColor,
    borderRadius: 16,
    padding: '24px 40px',
    gap: 12,
    overflow: 'hidden',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    [desktopFirstMediaQuery.mobile]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: 20,
    },
  });

const styleCardImage = css({
  flexShrink: 0,
  order: 1,
  [desktopFirstMediaQuery.mobile]: {
    order: -1,
  },
});

const styleImage = css({
  width: 100,
  height: 100,
  [desktopFirstMediaQuery.mobile]: {
    width: 48,
    height: 48,
  },
});

const styleImagePlaceholder = css({
  width: 100,
  height: 100,
  borderRadius: 12,
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  [desktopFirstMediaQuery.mobile]: {
    width: 48,
    height: 48,
  },
});

const styleCardTextContent = css({
  order: 0,
});

const styleCardTitle = css({
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 16,
  },
});

const styleCardDesc = css({
  fontSize: 18,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 14,
  },
});
